import * as React from 'react';
import { Button } from '../primitives/button';
import { cn } from '../../lib/utils';

export interface EmptyStateAction {
  label: string;
  onAction: () => void;
}

export interface EmptyStateProps {
  title: string;
  /** Why the screen is empty, and what fills it. */
  description?: React.ReactNode;
  /** Any icon component that accepts a className — lucide or heroicons. */
  icon?: React.ComponentType<{ className?: string }>;
  /**
   * `primary` for a screen waiting to be filled — the icon is an invitation.
   * `muted` (the default) for one that is empty by circumstance.
   */
  tone?: 'muted' | 'primary';
  /** One action only — an empty screen shouldn't present a decision. */
  action?: EmptyStateAction;
  /** Quieter follow-up, e.g. a link to an explanation. */
  secondary?: React.ReactNode;
  /**
   * Shown between the description and the action — used by the no-results
   * state to put the active filters where they can be removed, rather than
   * telling someone to go back up the page and find them.
   */
  children?: React.ReactNode;
  size?: 'sm' | 'md';
  testId?: string;
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon: Icon,
  tone = 'muted',
  action,
  secondary,
  children,
  size = 'md',
  testId,
  className,
}: EmptyStateProps) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex flex-col items-center justify-center px-6 text-center',
        size === 'md' ? 'py-16' : 'py-10',
        className,
      )}
    >
      {Icon && (
        <span
          className={cn(
            'mb-3 flex items-center justify-center rounded-full',
            size === 'md' ? 'h-[52px] w-[52px] p-3.5' : 'h-11 w-11 p-2.5',
            tone === 'primary' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          <Icon className="h-full w-full" />
        </span>
      )}
      {/*
        Spike scale: a 15px heading over 13px body, not 18 over 14. An empty
        screen has nothing to compete with, so an oversized heading there reads
        louder than the page titles on screens that do have content.

        A real heading, not a styled <p>: this is the only title on an otherwise
        blank screen, so it is exactly what someone navigating by headings needs
        to land on. The scale above is unaffected.
      */}
      <h2 className={cn('font-semibold text-foreground', size === 'md' ? 'text-[15px]' : 'text-sm')}>
        {title}
      </h2>
      {description && (
        <p className="mt-1.5 max-w-sm text-[13px] leading-5 text-muted-foreground">{description}</p>
      )}
      {children && <div className="mt-4">{children}</div>}
      {action && (
        <Button className="mt-5" onClick={action.onAction}>
          {action.label}
        </Button>
      )}
      {secondary && <div className="mt-3 text-sm text-muted-foreground">{secondary}</div>}
    </div>
  );
}
